import { SyntheticEvent } from "react";
import { ListViewSchema } from "./Common";

import styles from "./BoardListViewItem.module.css";

interface ListViewItemProps {
    cols: number;
    schema: ListViewSchema[];
    data: any;

    onMouseOver?: (event: SyntheticEvent) => void;
    onMouseOut?: (event: SyntheticEvent) => void;
    onClick?: (item: object, event: SyntheticEvent) => void;

    background?: string;
    color?: string;
}
/**
 * @author khy
 * @date 2022-08-31 16:33
 * @remark 테이블 행 컴포넌트
 * @param cols (required)
 * 테이블 컬럼의 갯수
 * @param schema (required)
 * 컴포넌트를 만들기 위한 schema
 * @param data (required)
 * 컴포넌트를 만들기 위한 data, schema 와 일치하는 property 만 표현됨
 * @param onMouseOver (optional)
 * 행 마우스 오버 이벤트 함수
 * @param onMouseOut (optional)
 * 행 마우스 아웃 이벤트 함수
 * @param onClick (optional)
 * 행 클릭 이벤트 함수
 * @param background (optional)
 * row background CSS
 * @param color (optional)
 * row text color CSS
 * @returns ListViewItem JSX.Element
 */
function ListViewItem(props: ListViewItemProps) {
    /**
     * renderItem 들의 Col width 을 설정해주기 위한 함수
     * @param widthRatios ex) [ 1.5, 4, 1, 3, 0.5 ] col width
     * @return ex) [ 15, 40, 10, 30, 5 ] // widthRatios 를 총합 100% 이내로 각각 비율에 맞게 나눠 줌
     */
    const resizeColumnWidth = (widthRatios: number[]) => {
        const divide = widthRatios.reduce((prev, current) => prev + current, 0);
        const per = 100 / divide;
        const widths = widthRatios.map((e) => (e * per).toFixed(2));

        return widths;
    };

    const widthRatios = props.schema ? props.schema.map((e) => (e.widthRatio as number) ?? 1) : [1]; // columns 에 widthRatio 가 없으면 기본 비율을 1로 배정함
    const widths = resizeColumnWidth(widthRatios);
    const rowStyle = props.onClick
        ? { cursor: "pointer", background: props.background, color: props.color }
        : { background: props.background, color: props.color };

    const handleMouseOver = (event: SyntheticEvent) => {
        if (props.onMouseOver) {
            props.onMouseOver(event);
        } else {
            return undefined;
        }
    };

    const handleMouseOut = (event: SyntheticEvent) => {
        if (props.onMouseOut) {
            props.onMouseOut(event);
        } else {
            return undefined;
        }
    };

    const handleMouseClick = (event: SyntheticEvent) => {
        if (props.onClick) {
            props.onClick(props.data, event);
        } else {
            return undefined;
        }
    };

    return (
        <div
            className={styles.row}
            style={rowStyle}
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
            onClick={handleMouseClick.bind(null, props.data)}
        >
            {Array.from(Array(props.cols)).map((empty, colIndex) => {
                const column = props.schema ? props.schema[colIndex] : undefined;
                const width = widths[colIndex] ? `${widths[colIndex]}%` : undefined;
                const columnStyle = column?.style ? { width, ...column.style } : { width };

                if (column && widths.length > 0 && props.data) {
                    const value = column.expression ? column.expression : props.data?.[column.field] ?? "";
                    const className = column.onClick ? `${styles.col} ${styles.button}` : styles.col;

                    const handleColumnClick = (event: SyntheticEvent) => {
                        if (column.onClick) {
                            event.preventDefault();
                            event.stopPropagation();
                            column.onClick(props.data, event);
                        } else {
                            return undefined;
                        }
                    };

                    return (
                        <div key={`${colIndex}]`} className={className} onClick={handleColumnClick} style={columnStyle}>
                            {value}
                        </div>
                    );
                } else {
                    return <div key={`${colIndex}]`} className={styles.col} style={columnStyle}></div>;
                }
            })}
        </div>
    );
}

export default ListViewItem;

ListViewItem.defaultProps = {
    background: "white",
    color: "black",
};
